import { Suspense, useContext, useMemo } from 'react';
import { SkeletonMainLayout } from 'src/modules/common/skeletons/SkeletonMainLayout/SkeletonMainLayout.tsx';
import { Query } from 'src/modules/common/components/Query/Query.tsx';
import { useNavigate } from 'react-router-dom';
import { ConfigContext } from 'src/configContext.ts';
import { useFilterLogic } from 'src/modules/common/hooks/useFilterLogic.ts';
import { useCheckMainFilter } from 'src/modules/common/hooks/useCheckMainFilter.ts';
import {
    AccessoriesPageData,
    AccessoriesPageVariables,
    getAccessoriesPageConfig,
} from 'src/modules/accessories/AccessoriesPageMapping.ts';
import { skipToken } from '@apollo/client';
import { composeFilter } from 'src/utils/composeFilter.ts';
import { AccessoriesPage } from 'src/modules/accessories/AccessoriesPage.tsx';
import { useUserInputState } from '../user-input/useUserInputState';

export const PrefetchAccessories = () => {
    const { userSettings, accessoryPageMandatoryFilterConfig } = useContext(ConfigContext);
    const pageConfig = useMemo(() => getAccessoriesPageConfig(userSettings.currentRegion), [userSettings]);
    if (pageConfig === null) {
        throw new Error('No Page Config!');
    }

    const navigate = useNavigate();

    const { searchParams, locationState } = useUserInputState([]);

    const [
        filter,
    ] = useFilterLogic([
        ...pageConfig.mainFilterConfig,
        ...pageConfig.additionalFilterConfig,
        ...pageConfig.actuatorsFilterConfig,
    ], searchParams);

    useCheckMainFilter({
        filter,
        searchParams,
        navigate,
        filterConfig: [],
    });

    const variables = useMemo((): {
        variables: AccessoriesPageVariables;
    } | typeof skipToken => {
        if (filter === null) {
            return skipToken;
        }
        return {
            variables: {
                filter: composeFilter(
                    accessoryPageMandatoryFilterConfig.config,
                    accessoryPageMandatoryFilterConfig.values,
                    userSettings.currentLocale,
                ),
                actuatorLabel: locationState.actuatorLabel,
                valveLabel: locationState.valveLabel,
                locale: userSettings.currentLocale,
            },
        };
    }, [
        accessoryPageMandatoryFilterConfig,
        filter,
        locationState,
        userSettings,
    ]);

    return (
        <Suspense fallback={<SkeletonMainLayout breadcrumbs={false}/>}>
            <Query<AccessoriesPageData, AccessoriesPageVariables>
                query={pageConfig.query}
                variables={variables}
            >
                {({ data }) => {
                    if (data === undefined) {
                        return (<SkeletonMainLayout/>);
                    }
                    return (
                        <AccessoriesPage
                            data={data}
                            filter={filter}
                        />
                    );
                }}
            </Query>
        </Suspense>
    );
};
